import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Dice5, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { LastResults } from "../components/LastResults";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

// Lucky Dice: roll 2 dice. Bet Under 7 (2x), Lucky 7 (5x) or Over 7 (2x).
const BETS = [
  { id: "under", label: "Under 7", hint: "Sum 2–6", mult: 2 },
  { id: "seven", label: "Lucky 7", hint: "Sum = 7", mult: 5 },
  { id: "over", label: "Over 7", hint: "Sum 8–12", mult: 2 },
];
const PIPS = {
  1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8],
};

function Die({ value }) {
  const active = new Set(PIPS[value] || []);
  return (
    <div className="grid grid-cols-3 grid-rows-3 gap-1 h-20 w-20 rounded-2xl p-2.5" data-testid="dice-face"
      style={{
        background: "radial-gradient(circle at 32% 26%, #ffffff 0%, #f7f3e8 55%, #ddd6c2 100%)",
        border: "1px solid rgba(217,181,69,0.55)",
        boxShadow: "0 8px 16px rgba(0,0,0,0.5), inset -3px -4px 8px rgba(0,0,0,0.14), inset 2px 3px 6px rgba(255,255,255,0.9)",
      }}>
      {Array.from({ length: 9 }).map((_, i) => (
        <span key={i} className="rounded-full"
          style={active.has(i) ? { background: "radial-gradient(circle at 35% 30%, #3d3d46, #0a0a0d)", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.6)" } : undefined} />
      ))}
    </div>
  );
}

export default function LuckyDice() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [pick, setPick] = useState("under");
  const [dice, setDice] = useState([1, 1]);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [lastBet, setLastBet] = useState(null);
  const streakRef = useRef(0);
  const anim = useRef(null);
  const diceColor = (sum) => (sum === 7 ? "gold" : sum < 7 ? "red" : "green");
  const [recent, setRecent] = useState(() =>
    Array.from({ length: 50 }, () => {
      const sum = 2 + Math.floor(Math.random() * 6) + Math.floor(Math.random() * 6);
      return { label: sum, color: sum === 7 ? "gold" : sum < 7 ? "red" : "green" };
    })
  );

  const rebet = () => {
    if (rolling || !lastBet) return;
    setPick(lastBet.pick);
    setStake(String(lastBet.stake));
    roll(lastBet);
  };

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="dice-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="dice-go-home">Go Home</Button></Link>
    </div>
  );

  const roll = async (override) => {
    const pk = override ? override.pick : pick;
    const s = override ? Number(override.stake) : Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio();
    setRolling(true); setResult(null);
    let res;
    try {
      res = await gamesApi.dice({ stake: s, pick: pk });
    } catch (e) {
      setRolling(false);
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
      return;
    }
    setLastBet({ stake: s, pick: pk });
    const [d1, d2] = res.result.dice; // server-decided
    const sum = res.result.sum;
    let ticks = 0;
    anim.current = setInterval(() => {
      setDice([1 + Math.floor(Math.random() * 6), 1 + Math.floor(Math.random() * 6)]);
      ticks += 1;
      if (ticks >= 12) {
        clearInterval(anim.current);
        setDice([d1, d2]);
        finish(res, sum);
      }
    }, 90);
  };

  const finish = async (res, sum) => {
    await refresh();
    const won = res.payout > 0;
    setResult({ sum, won, payout: res.payout });
    setRecent((prev) => [{ label: sum, color: diceColor(sum) }, ...prev].slice(0, 50));
    streakRef.current = won ? streakRef.current + 1 : 0;
    if (won) playCoinSplash();
    setReveal({ label: sum, won, payout: res.payout, streak: streakRef.current });
    setRolling(false);
  };

  const chipLabel = (v) => { const n = Number(v); return n >= 1000 ? (Number.isInteger(n / 1000) ? n / 1000 : (n / 1000).toFixed(1)) + "K" : String(n); };
  const Chip = ({ amt }) => {
    const n = Number(amt);
    const special = n >= 10000;
    const size = special ? 42 : n >= 5000 ? 34 : 30;
    return (
      <span className="absolute right-1 top-1 z-20 grid place-items-center rounded-full font-extrabold text-black pointer-events-none"
        style={{
          height: size, width: size, fontSize: special ? 13 : 11,
          background: special ? "radial-gradient(circle at 50% 30%, #fff7cc, #f5b90b 55%, #b45309)" : "radial-gradient(circle at 50% 32%, #fde68a, #f59e0b)",
          border: special ? "2px solid #fff7cc" : "2px dashed rgba(0,0,0,0.6)",
          boxShadow: special ? "0 0 14px rgba(245,158,11,0.95), 0 2px 6px rgba(0,0,0,0.55)" : "0 2px 5px rgba(0,0,0,0.5)",
        }}>{chipLabel(amt)}</span>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="dice-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Lucky Dice</h1>
          <p className="text-sm text-muted-foreground">Roll two dice · beat the seven</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="dice-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Dice arena — emerald felt & gold */}
      <div className="relative rounded-2xl p-8 flex flex-col items-center overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 130% 95% at 50% 18%, #0d7a48 0%, #085c36 48%, #032e1c 100%)",
          border: "1px solid rgba(217,181,69,0.6)",
          boxShadow: "inset 0 0 0 3px rgba(217,181,69,0.3), inset 0 0 70px rgba(0,0,0,0.45), 0 10px 30px rgba(0,0,0,0.45)",
        }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 35% at 30% 10%, rgba(255,255,255,0.12), transparent 60%)" }} />
        <LastResults items={recent.slice(0, 10)} testId="dice-last-results" />
        <div className={`relative flex gap-4 ${rolling ? "animate-bounce" : ""}`}>
          <Die value={dice[0]} />
          <Die value={dice[1]} />
        </div>
        <div className="relative mt-5 h-7 text-center" data-testid="dice-result">
          {result ? (
            <span className="font-semibold">
              Rolled <span className="font-display font-bold text-lg text-yellow-300">{result.sum}</span>
              {result.won && <span className="text-yellow-300"> · Won {inr(result.payout)}</span>}
            </span>
          ) : (
            <span className="text-sm text-emerald-100/70">{rolling ? "Rolling…" : "Pick a side and roll"}</span>
          )}
        </div>
        <Button className="relative mt-4 font-semibold h-12 px-12 w-full sm:w-auto text-base" onClick={() => roll()} disabled={rolling} data-testid="dice-roll-btn">
          <Dice5 className={`h-4 w-4 mr-1.5 ${rolling ? "animate-spin" : ""}`} /> {rolling ? "Rolling…" : "Roll"}
        </Button>
      </div>

      {/* Bet table */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="dice-table">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your bet</div>
        <div className="grid grid-cols-3 gap-2">
          {BETS.map((b) => (
            <button key={b.id} data-testid={`dice-bet-${b.id}`} onClick={() => setPick(b.id)}
              className={`relative rounded-xl py-4 px-2 text-center transition-[color,background-color,border-color,opacity,transform] ${
                pick === b.id ? "ring-2 ring-yellow-400/90" : "bg-secondary hover:bg-secondary/70"
              }`}
              style={pick === b.id ? {
                background: "radial-gradient(ellipse 120% 100% at 50% 0%, #0d7a48 0%, #064a2b 100%)",
                boxShadow: "inset 0 0 20px rgba(0,0,0,0.35), 0 0 12px rgba(250,204,21,0.25)",
              } : undefined}>
              <div className="font-display font-bold text-base">{b.label}</div>
              <div className={`text-[11px] ${pick === b.id ? "text-emerald-100/70" : "text-muted-foreground"}`}>{b.hint}</div>
              <div className={`mt-1 text-sm font-bold ${pick === b.id ? "text-yellow-300" : "text-primary"}`}>{b.mult}x</div>
              {pick === b.id && Number(stake) > 0 && <Chip amt={stake} />}
            </button>
          ))}
        </div>

        {lastBet && (
          <button onClick={rebet} disabled={rolling} data-testid="dice-rebet-btn"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-semibold hover:bg-primary/25 disabled:opacity-40 transition-colors">
            <RotateCw className="h-3.5 w-3.5" /> Rebet ₹{lastBet.stake}
          </button>
        )}

        <div className="mt-6">
          <div className="flex-1">
            <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
            <Input type="number" min={20} max={100000} value={stake} onChange={(e) => setStake(e.target.value)} className="mt-1.5" data-testid="dice-stake-input" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
                const special = q === 10000;
                return (
                  <button key={q} onClick={() => setStake(String(q))} data-testid={`dice-chip-${q}`}
                    className={special
                      ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 transition-[color,background-color,border-color,opacity,transform]"
                      : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70"}
                    style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                    +{q}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
